import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { submitContact } from '@/api/contact';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const schema = z.object({
  name: z.string().min(1, 'Required'),
  email: z.string().email('Valid email required'),
  message: z.string().min(5, 'Tell us a bit more'),
});
type Values = z.infer<typeof schema>;

export function Contact() {
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  if (sent) {
    return (
      <section className="mx-auto w-full max-w-lg px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Thanks — we'll be in touch.</h1>
        <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
          Your message was received. Someone from the Ivrello team will reach out shortly.
        </p>
        <div className="mt-6">
          <Button asChild variant="outline" size="sm">
            <Link to="/">Back to home</Link>
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto w-full max-w-lg px-4 sm:px-6 lg:px-8 py-12 sm:py-16 space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Contact us</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Tell us who you are and what you're looking for.
        </p>
      </header>

      <form
        className="space-y-4"
        noValidate
        onSubmit={handleSubmit(async (v) => {
          setSubmitError(null);
          try {
            await submitContact(v);
            setSent(true);
          } catch {
            setSubmitError('Something went wrong — please try again or email us directly.');
          }
        })}
      >
        {/* Name */}
        <div className="space-y-1">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            aria-invalid={errors.name != null}
            aria-describedby={errors.name ? 'name-error' : undefined}
            {...register('name')}
          />
          {errors.name && (
            <p id="name-error" role="alert" className="text-xs text-destructive">
              {errors.name.message}
            </p>
          )}
        </div>

        {/* Email */}
        <div className="space-y-1">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            aria-invalid={errors.email != null}
            aria-describedby={errors.email ? 'email-error' : undefined}
            {...register('email')}
          />
          {errors.email && (
            <p id="email-error" role="alert" className="text-xs text-destructive">
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Message */}
        <div className="space-y-1">
          <Label htmlFor="message">Message</Label>
          <Textarea
            id="message"
            rows={5}
            aria-invalid={errors.message != null}
            aria-describedby={errors.message ? 'message-error' : undefined}
            {...register('message')}
          />
          {errors.message && (
            <p id="message-error" role="alert" className="text-xs text-destructive">
              {errors.message.message}
            </p>
          )}
        </div>

        {/* Form-level error */}
        {submitError && (
          <p role="alert" className="text-sm text-destructive">
            {submitError}
          </p>
        )}

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Sending…' : 'Send message'}
        </Button>
      </form>
    </section>
  );
}
